import { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

export const useDrillManagement = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [drillText, setDrillText] = useState("");
  const [savedDrills, setSavedDrills] = useState<any[]>([]);

  const fetchSavedDrills = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const { data, error } = await supabase
        .from("drills")
        .select("*")
        .eq("creator_id", user.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setSavedDrills(data || []);
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch saved drills.",
      });
    }
  };

  const handleCreateDrill = async (generatedImage: string | null) => {
    if (!drillText.trim()) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please enter drill instructions.",
      });
      return;
    }

    setLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      // Generate a title from the first few words of the drill text
      const title = drillText.split(' ').slice(0, 5).join(' ') + '...';

      const { error } = await supabase.from("drills").insert({
        title: title,
        description: drillText,
        price: 9.99,
        drill_data: { content: drillText },
        creator_id: user.id,
        preview_image_url: generatedImage
      });

      if (error) throw error;

      toast({
        title: "Success",
        description: "Drill created successfully!",
      });
      
      setDrillText("");
      fetchSavedDrills();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to create drill. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDrill = async (drillId: string) => {
    try {
      const { error } = await supabase
        .from("drills")
        .delete()
        .eq("id", drillId);

      if (error) throw error;

      toast({
        title: "Success",
        description: "Drill deleted successfully!",
      });
      fetchSavedDrills();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to delete drill. Please try again.",
      });
    }
  };

  useEffect(() => {
    fetchSavedDrills();
  }, []);

  return {
    loading,
    drillText,
    setDrillText,
    savedDrills,
    handleCreateDrill,
    handleDeleteDrill,
  };
};